'use strict';

import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import DecimalInput from './DecimalInput.react';
import { convertWeight } from '../../utils/Math';

import './WeightInput.scss';

/**
 * WeightInput - A unified weight input component that handles kg/lbs conversion automatically
 *
 * Features:
 * - Accepts value in kg (canonical unit used by backend)
 * - Displays in kg or lbs based on unitsMode
 * - Automatic bidirectional conversion
 * - Uses DecimalInput internally with 1 decimal place
 * - Returns both kg and lbs values via onChange
 *
 * Usage:
 * <WeightInput
 *     valueKg={user.weight_kg}
 *     unitsMode={user.units_mode}
 *     onChange={({ weight_kg, weight_lbs }) => handleChange(weight_kg, weight_lbs)}
 * />
 */
const WeightInput = ({
    valueKg,
    valueLbs,
    unitsMode = 'metric',
    onChange,
    onFocus,
    onBlur,
    min = 0,
    max,
    placeholder,
    disabled = false,
    showLabel = true,
    className = 'with-units',
    id,
    rejectZero = false,
    'data-testid': dataTestId,
}) => {
    const isMetric = unitsMode === 'metric';

    // Generate a unique ID for accessibility if not provided
    const inputId = id || `weight-input-${isMetric ? 'kg' : 'lbs'}`;

    // Default max based on unit (reasonable human weight limits)
    const effectiveMax = max ?? (isMetric ? 500 : 1100);

    // Compute display value based on units mode
    // - null/undefined/'' = empty field (cleared or not set)
    // - 0 with rejectZero = treat as empty (for optional fields like goal_weight)
    // - 0 without rejectZero = show "0" (user typed zero, validation error will show)
    const displayValue = useMemo(() => {
        if (isMetric) {
            // Metric mode: use kg value directly
            if (valueKg === '' || valueKg === null || valueKg === undefined) {
                return '';
            }
            if (rejectZero && (valueKg === 0 || valueKg === '0')) {
                return '';
            }
            return valueKg;
        } else {
            // English mode: prefer explicit lbs value, fallback to converted kg
            if (valueLbs !== undefined && valueLbs !== null && valueLbs !== '') {
                if (rejectZero && (valueLbs === 0 || valueLbs === '0')) {
                    return '';
                }
                return valueLbs;
            }
            if (valueKg === '' || valueKg === null || valueKg === undefined) {
                return '';
            }
            if (rejectZero && (valueKg === 0 || valueKg === '0')) {
                return '';
            }
            return convertWeight(valueKg, 'kg', 'lbs', 1);
        }
    }, [valueKg, valueLbs, isMetric, rejectZero]);

    const handleChange = useCallback((newValue) => {
        // Empty/cleared field: send null to state (prepareWeightForSave converts to 0 for API)
        if (newValue === '' || newValue === null || newValue === undefined) {
            onChange && onChange({ weight_kg: null, weight_lbs: null, cleared: true });
            return;
        }

        // Preserve trailing decimal point during typing (e.g., "145." -> allow user to continue typing)
        // by keeping the raw string value. parseFloat("145.") returns 145, losing the decimal point.
        const stringValue = String(newValue);
        const isPartialDecimal = stringValue.endsWith('.') || stringValue === '.';

        const numValue = parseFloat(newValue);
        if (isNaN(numValue) && !isPartialDecimal) {
            onChange && onChange({ weight_kg: null, weight_lbs: null, cleared: true });
            return;
        }

        // For partial decimals (e.g., "145."), pass the string to preserve the decimal point
        // The parent component should handle this gracefully
        const valueToUse = isPartialDecimal ? stringValue : numValue;

        if (isMetric) {
            // User typed in kg
            const weight_kg = valueToUse;
            const weight_lbs = isPartialDecimal ? null : convertWeight(numValue, 'kg', 'lbs', 1);
            onChange && onChange({ weight_kg, weight_lbs });
        } else {
            // User typed in lbs
            const weight_lbs = valueToUse;
            const weight_kg = isPartialDecimal ? null : convertWeight(numValue, 'lbs', 'kg', 1);
            onChange && onChange({ weight_kg, weight_lbs });
        }
    }, [isMetric, onChange]);

    const unitLabel = isMetric ? 'kg' : 'lbs';
    const unitClass = isMetric ? 'weight-kg' : 'weight-lbs';

    return (
        <div className={`weight-input-container ${unitClass} ${className}`.trim()}>
            <DecimalInput
                id={inputId}
                value={displayValue}
                onChange={handleChange}
                onFocus={onFocus}
                onBlur={onBlur}
                min={min}
                max={effectiveMax}
                decimals={1}
                placeholder={placeholder}
                disabled={disabled}
                data-testid={dataTestId}
                className="weight-input"
            />
            {showLabel && (
                <label htmlFor={inputId} className="weight-unit-label">{unitLabel}</label>
            )}
        </div>
    );
};

WeightInput.propTypes = {
    /** Weight value in kilograms (canonical unit) */
    valueKg: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Weight value in pounds (optional, used for display in english mode) */
    valueLbs: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Units mode: 'metric' for kg, 'english' for lbs */
    unitsMode: PropTypes.oneOf(['metric', 'english']),
    /** Callback with { weight_kg: number, weight_lbs: number } - both are always numbers */
    onChange: PropTypes.func,
    /** Focus event handler */
    onFocus: PropTypes.func,
    /** Blur event handler */
    onBlur: PropTypes.func,
    /** Minimum allowed value (in display unit) */
    min: PropTypes.number,
    /** Maximum allowed value (in display unit) */
    max: PropTypes.number,
    /** Placeholder text */
    placeholder: PropTypes.string,
    /** Disable input */
    disabled: PropTypes.bool,
    /** Show unit label (kg/lbs) */
    showLabel: PropTypes.bool,
    /** Additional CSS class */
    className: PropTypes.string,
    /** ID for the input element (for accessibility) */
    id: PropTypes.string,
    /** Treat 0 as empty (for optional fields like goal_weight) */
    rejectZero: PropTypes.bool,
    /** Test ID for testing */
    'data-testid': PropTypes.string,
};

WeightInput.defaultProps = {
    valueKg: '',
    valueLbs: '',
    unitsMode: 'metric',
    min: 0,
    disabled: false,
    showLabel: true,
    className: 'with-units',
    rejectZero: false,
};

export default WeightInput;
